import { useMemo, useState } from "react";
import useVademecum from "../../hooks/useVademecum";
import PresentationWizardDialog from "./WizardDialog";
import { defaultPresentation, STEP_TITLES, STEPS } from "./utils";
import BoxPresentationPicker from "../components/PresentationPicker";
import BoxQuantityPicker from "../components/QuantityPicker";
import PowerPicker from "../components/PowerPicker";
import _ from "lodash";

const useDrugPresentations = (medicine, presentationPower) => {
  const vademecum = useVademecum();
  return useMemo(() => {
    const nonAccessoryPresentations = _(vademecum)
      .filter({ principioActivo: medicine.principioActivo })
      .flatMap(medicine => _.map(medicine.presentaciones, presentation => 
        _.assign({}, presentation, { displayName: `${presentation.name} (${medicine.marcaComercial})` })))
      .filter(presentation => !presentationPower || (presentation.power?.value === presentationPower.power && presentation.power?.unit === presentationPower.unit) || (presentation.name === presentationPower.power));
    const accessoryPresentations = _.map(medicine.presentaciones, presentation =>  _.assign({}, presentation, { displayName: presentation.name }));
    const presentationsToUse = medicine.principioActivo === "accesorio" ? accessoryPresentations : nonAccessoryPresentations;

    return _(presentationsToUse)
      .uniqBy("regNo")
      .compact()
      .value()
  }
  , [medicine, presentationPower, vademecum]);
};

const useDrugPowers = (medicine) => {
  const vademecum = useVademecum();
  return useMemo(() => 
    _(vademecum)
      .filter({ principioActivo: medicine.principioActivo })
      .flatMap(med => _(med.presentaciones).map(presentation => {
        if (!presentation.power) return { power: presentation.name, unit: '' };
        const { power: { value, unit } } = presentation;
        return { power: value, unit };
      }).value())
      .uniqBy(power => `${power.power}${power.unit}`)
      .compact()
      .value()
  , [medicine, vademecum]);
};

const DrugBoxPresentationPicker = ({ medicine, presentationPower, onSelect }) => {
  const presentations = useDrugPresentations(medicine, presentationPower);
  return <BoxPresentationPicker presentations={presentations} onSelect={onSelect} noAutopick />;
};

const DrugPowerPicker = ({ medicine, onSelect }) => {
  const powers = useDrugPowers(medicine);
  return <PowerPicker powers={powers} onSelect={onSelect} />;
};


const DrugSelectionStrategy = ({ medicine, onComplete, onClose }) => {
  const [presentation, setPresentation] = useState(defaultPresentation);
  const [presentationPower, setPresentationPower] = useState(null);
  const powers = useDrugPowers(medicine);

  const getCurrentStep = () => {
    if (presentation.name) return STEPS.QUANTITY;
    if (powers.length === 0 || presentationPower || _.size(powers) === 1) return STEPS.PRESENTATION;
    return STEPS.POWER;
  };

  const stepComponents = {
    [STEPS.QUANTITY]: (
      <BoxQuantityPicker 
        onSelect={quantity => {
          onComplete(quantity, presentation);
        }} 
      />
    ),
    [STEPS.PRESENTATION]: (
      <DrugBoxPresentationPicker 
        medicine={medicine}
        presentationPower={presentationPower}
        onSelect={setPresentation}
      />
    ),
    [STEPS.POWER]: (
      <DrugPowerPicker 
        medicine={medicine}
        onSelect={setPresentationPower}
      />
    )
  };

  const currentStep = getCurrentStep();

  return (
    <PresentationWizardDialog
      open
      onClose={onClose}
      title={STEP_TITLES[currentStep]}
    >
      {stepComponents[currentStep]}
    </PresentationWizardDialog>
  );
};

export default DrugSelectionStrategy;